import { dedupeSalesByEvent } from "@repo/types/valuation";
import type { Property, PropertySnapshot } from "@/lib/api";

type SaleRow = Pick<
  PropertySnapshot,
  "id" | "listing_type" | "sale_date" | "sale_price" | "snapshot_date" | "raw_data"
>;

export function getSaleHistory<T extends SaleRow>(snapshots: T[]): T[] {
  const candidates = snapshots
    .filter(
      (row) =>
        row.listing_type?.toLowerCase() === "sold" &&
        row.sale_date &&
        row.sale_price &&
        row.sale_price > 0,
    )
    .sort((a, b) => b.snapshot_date.localeCompare(a.snapshot_date));
  const keys = candidates.map((row) => ({
    saleDate: row.sale_date as string,
    salePrice: row.sale_price as number,
    saleEventKey:
      typeof row.raw_data?.sqmSaleEventKey === "string" ? row.raw_data.sqmSaleEventKey : null,
  }));
  const seen = new Set(dedupeSalesByEvent(keys).deduped);
  return candidates
    .filter((_, index) => seen.has(keys[index]))
    .sort((a, b) => (b.sale_date as string).localeCompare(a.sale_date as string));
}

type CompletenessProperty = Pick<
  Property,
  | "full_address"
  | "suburb"
  | "state"
  | "postcode"
  | "property_type"
  | "bedrooms"
  | "bathrooms"
  | "parking"
  | "latitude"
  | "longitude"
>;

export function getCompleteness(property: CompletenessProperty, sales: SaleRow[]) {
  const fields: Array<[string, boolean]> = [
    ["address", !!property.full_address],
    ["suburb", !!property.suburb],
    ["state", !!property.state],
    ["postcode", !!property.postcode],
    ["property type", !!property.property_type],
    ["bedrooms", property.bedrooms != null],
    ["bathrooms", property.bathrooms != null],
    ["parking", property.parking != null],
    ["sale history", sales.length > 0],
  ];
  const missing = fields.filter(([, present]) => !present).map(([name]) => name);
  const flags: string[] = [];
  if (property.bedrooms != null && property.bedrooms < 0) flags.push("Bedrooms below zero");
  if (property.bathrooms != null && property.bathrooms < 0) flags.push("Bathrooms below zero");
  if (property.parking != null && property.parking < 0) flags.push("Parking below zero");
  if (
    (property.latitude != null && (property.latitude < -90 || property.latitude > 90)) ||
    (property.longitude != null && (property.longitude < -180 || property.longitude > 180))
  ) {
    flags.push("Coordinates outside valid range");
  }
  if ((property.latitude == null) !== (property.longitude == null))
    flags.push("Incomplete coordinates");
  return { present: fields.length - missing.length, total: fields.length, missing, flags };
}

type MonthlyPrice = { sale_month: string; average_price: number | null; sample_count: number };

/** Index an observed sale using monthly suburb house averages only when the full chain exists. */
export function indexLastSale(
  price: number | null,
  date: string | null,
  months: MonthlyPrice[],
): number | null {
  if (!price || price <= 0 || !date || !/^\d{4}-\d{2}/.test(date)) return null;
  const ordered = [...months].sort((a, b) => a.sale_month.localeCompare(b.sale_month));
  const saleMonth = date.slice(0, 7);
  const start = ordered.findIndex((month) => month.sale_month.slice(0, 7) === saleMonth);
  if (start < 0 || start === ordered.length - 1) return null;
  const chain = ordered.slice(start);
  if (
    chain.some(
      (month) => !month.average_price || month.average_price <= 0 || month.sample_count <= 0,
    )
  )
    return null;
  for (let i = 1; i < chain.length; i++) {
    const previous = chain[i - 1].sale_month.slice(0, 7);
    const expected = new Date(
      Date.UTC(Number(previous.slice(0, 4)), Number(previous.slice(5, 7)), 1),
    );
    if (chain[i].sale_month.slice(0, 7) !== expected.toISOString().slice(0, 7)) return null;
  }
  return Math.round(
    (price * (chain[chain.length - 1].average_price as number)) /
      (chain[0].average_price as number),
  );
}
